import { useState } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import {
  acceptAll,
  rejectOptional,
  saveCustomPreferences,
} from '@/hooks/useCookieConsent';
import type { CookiePreferences } from '@/hooks/useCookieConsent';
import { cn } from '@/lib/cn';

type Tab = 'overview' | 'essential' | 'optional';

const TABS = [
  { id: 'overview', label: 'Visão geral' },
  { id: 'essential', label: 'Essenciais' },
  { id: 'optional', label: 'Opcionais' },
] as const;

interface CookiePrefModalProps {
  open: boolean;
  onClose: () => void;
  /** Current persisted preferences — used to seed the toggles when opening. */
  prefs: CookiePreferences | null;
}

/**
 * Tabbed cookie preferences modal — opened from the LGPD consent banner.
 * Each save action persists and closes; cancel/close keeps the banner open.
 */
export function CookiePrefModal({ open, onClose, prefs }: CookiePrefModalProps) {
  const [tab, setTab] = useState<Tab>('overview');
  const [analytics, setAnalytics] = useState(prefs?.analytics ?? false);
  const [marketing, setMarketing] = useState(prefs?.marketing ?? false);

  const handleSave = () => {
    saveCustomPreferences({ analytics, marketing });
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Preferências de cookies"
      maxWidth="max-w-3xl"
    >
      <div className="flex flex-col gap-6">
        <div
          role="tablist"
          aria-label="Categorias de cookies"
          className="flex gap-2 border-b border-brand-lightgray"
        >
          {TABS.map((t) => (
            <button
              key={t.id}
              role="tab"
              aria-selected={tab === t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={cn(
                '-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors',
                tab === t.id
                  ? 'border-brand-yellow text-brand-purple'
                  : 'border-transparent text-brand-purple/60 hover:text-brand-purple',
              )}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'overview' && (
          <div className="space-y-3 text-sm text-brand-purple/90 leading-relaxed">
            <p>
              A ARCOLL TECH usa cookies para garantir o funcionamento do site e,
              com seu consentimento, para melhorar sua experiência.
            </p>
            <p>
              Você pode aceitar todos, rejeitar os opcionais ou ajustar categoria
              por categoria nas abas ao lado.
            </p>
          </div>
        )}

        {tab === 'essential' && (
          <div className="space-y-4">
            <p className="text-sm text-brand-purple/90">
              Cookies essenciais são necessários para o funcionamento do site
              (autenticação, segurança, balanceamento de carga). Não podem ser
              desativados.
            </p>
            <Checkbox
              id="cookie-essential"
              label="Cookies essenciais — sempre ativos"
              checked
              disabled
              readOnly
            />
          </div>
        )}

        {tab === 'optional' && (
          <div className="space-y-4">
            <Checkbox
              id="cookie-analytics"
              label="Analytics — ajudam a entender como o site é usado"
              hint="Métricas agregadas, sem identificar você individualmente."
              checked={analytics}
              onChange={(e) => setAnalytics(e.target.checked)}
            />
            <Checkbox
              id="cookie-marketing"
              label="Marketing — conteúdo e comunicações mais relevantes"
              hint="Permite personalização de campanhas e anúncios."
              checked={marketing}
              onChange={(e) => setMarketing(e.target.checked)}
            />
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-2 justify-end border-t border-brand-lightgray pt-5">
          <Button variant="ghost-purple" onClick={() => rejectOptional()}>
            Rejeitar opcionais
          </Button>
          <Button variant="primary-purple" onClick={handleSave}>
            Salvar minhas escolhas
          </Button>
          <Button variant="primary-yellow" onClick={() => acceptAll()}>
            Aceitar todos
          </Button>
        </div>
      </div>
    </Modal>
  );
}
